import React, { useEffect, useState } from 'react';
import { docAssistSuggestions } from '../../utils/docAssist';
import { ipApi } from '../../services/api';
import { useHmsConfig } from '../../hooks/useHmsRecords';

/**
 * Clinical Assist — uses server AI when configured + hospital-enabled;
 * falls back to local rules. Never auto-writes EMR — doctor must click Apply.
 */
export default function DocAssistPanel({ complaint, age, gender, allergies, onApply }) {
  const { config: aiCfg } = useHmsConfig('ai-settings');
  const hospitalAiOn = aiCfg?.enabled !== false;

  const [suggestions, setSuggestions] = useState(null);
  const [source, setSource] = useState('rules');
  const [loading, setLoading] = useState(false);
  const [aiReady, setAiReady] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    ipApi.aiStatus()
      .then((s) => { if (!cancelled) setAiReady(!!s?.configured); })
      .catch(() => { if (!cancelled) setAiReady(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!complaint?.trim()) {
      setSuggestions(null);
      setErr('');
      return undefined;
    }
    const local = docAssistSuggestions(complaint, age);
    setSuggestions(local);
    setSource('rules');
    setErr('');

    if (aiReady === false || !hospitalAiOn) return undefined;

    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await ipApi.aiClinicalAssist({
          complaint: complaint.trim(),
          age,
          gender,
          allergies,
        });
        if (res?.suggestions) {
          setSuggestions(res.suggestions);
          setSource('ai');
          setErr('');
        }
      } catch (e) {
        if (e?.message?.includes('AI_NOT_CONFIGURED') || e?.message?.includes('not configured')) {
          setAiReady(false);
        } else {
          setErr(e.message || 'AI unavailable — showing rule suggestions');
        }
      } finally {
        setLoading(false);
      }
    }, 550);

    return () => clearTimeout(t);
  }, [complaint, age, gender, allergies, aiReady, hospitalAiOn]);

  if (!complaint?.trim()) {
    return (
      <div className="card doc-assist">
        <h4>Clinical Assist</h4>
        <p className="hint">Enter chief complaint for AI / smart suggestions.</p>
        {(aiReady === false || !hospitalAiOn) && (
          <p className="hint" style={{ fontSize: 11, marginTop: 6 }}>
            {!hospitalAiOn
              ? 'AI disabled for this hospital — using built-in rules.'
              : 'AI not configured yet — using built-in rules. Manager can enable under AI Assist.'}
          </p>
        )}
      </div>
    );
  }

  const s = suggestions || docAssistSuggestions(complaint, age);
  const label = loading
    ? 'AI thinking…'
    : (source === 'ai' && hospitalAiOn ? 'Powered by AI' : 'Rule-based');

  return (
    <div className="card doc-assist">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <h4 style={{ margin: 0 }}>Clinical Assist</h4>
        <span className="hint" style={{ fontSize: 10 }}>{label}</span>
      </div>
      <p className="hint" style={{ fontSize: 11 }}>
        Suggestions only — review before applying to EMR.
      </p>
      {err && <p className="hint" style={{ color: '#b45309', fontSize: 11 }}>{err}</p>}
      <div className="doc-assist-block">
        <strong>Diagnosis</strong>
        <div className="q-tag-row">{(s.diagnosis || []).map((d) => <span key={d} className="q-tag on">{d}</span>)}</div>
      </div>
      {(s.labs || []).length > 0 && (
        <div className="doc-assist-block">
          <strong>Investigations</strong>
          <div className="q-tag-row">{s.labs.map((l) => <span key={l} className="q-tag">{l}</span>)}</div>
        </div>
      )}
      {(s.rx || []).length > 0 && (
        <div className="doc-assist-block">
          <strong>Medicines</strong>
          <div className="q-tag-row">{s.rx.map((r) => <span key={r} className="q-tag">{r}</span>)}</div>
        </div>
      )}
      {(s.advice || []).length > 0 && (
        <div className="doc-assist-block">
          <strong>Advice</strong>
          <div className="q-tag-row">{s.advice.map((a) => <span key={a} className="q-tag">{a}</span>)}</div>
        </div>
      )}
      {(s.redFlags || []).length > 0 && (
        <div className="doc-assist-block">
          <strong style={{ color: '#b91c1c' }}>Red flags</strong>
          <div className="q-tag-row">{s.redFlags.map((a) => <span key={a} className="q-tag" style={{ borderColor: '#fecaca', color: '#b91c1c' }}>{a}</span>)}</div>
        </div>
      )}
      {onApply && (
        <button type="button" className="btn btn-sm btn-primary" style={{ marginTop: 8 }} disabled={loading} onClick={() => onApply(s)}>
          Apply to EMR
        </button>
      )}
    </div>
  );
}
